import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';
import { CheckCircle2, CreditCard, Wallet, Calendar, DollarSign, ShoppingCart, Camera, TrendingUp, PiggyBank, History } from 'lucide-react';

interface CreditLot {
  id: string;
  amount: number;
  expires_at: string;
  source: string;
  reference: string;
  created_at: string;
}

interface PaymentTransaction {
  id: string;
  transaction_number: string;
  credits_amount: number;
  price_amount: number;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface CreditUsage {
  id: string;
  amount: number;
  reason: string;
  created_at: string;
}

interface DailyStats {
  today_credits_used: number;
  week_credits_used: number;
  month_credits_used: number;
}

const AccountPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  
  const [credits, setCredits] = useState<number>(0);
  const [creditLots, setCreditLots] = useState<CreditLot[]>([]);
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [creditUsages, setCreditUsages] = useState<CreditUsage[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats>({
    today_credits_used: 0,
    week_credits_used: 0,
    month_credits_used: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'credits' | 'transactions' | 'usages'>('overview');

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchAccountData = async () => {
    if (!user) return;
    
    try {
      // Get available credits using FIFO system
      const { data: creditsData, error: creditsError } = await supabase.rpc(
        'get_user_available_credits',
        { p_user_id: user.id }
      );
      setCredits(creditsData || 0);

      // Get all credit lots with usage info
      const { data: allLots, error: lotsError } = await supabase
        .from('credit_lots')
        .select('*, credit_usages(amount)')
        .eq('user_id', user.id)
        .order('expires_at', { ascending: true });
      
      if (lotsError) {
        console.error('Error fetching credit lots:', lotsError);
      }
      
      // Filter to show only non-expired lots
      const nonExpiredLots = (allLots || []).filter(lot => new Date(lot.expires_at) > new Date());
      setCreditLots(nonExpiredLots);

      // Get successful payment transactions only
      const { data: payments } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'success')
        .order('created_at', { ascending: false })
        .limit(20);
      
      setTransactions(payments || []);

      // Get usage statistics
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);

      const { data: todayUsage } = await supabase
        .from('credit_usages')
        .select('amount')
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString());

      const todayCredits = todayUsage?.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0) || 0;

      const { data: weekUsage } = await supabase
        .from('credit_usages')
        .select('amount')
        .eq('user_id', user.id)
        .gte('created_at', weekAgo.toISOString());

      const weekCredits = weekUsage?.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0) || 0;

      const { data: monthUsage } = await supabase
        .from('credit_usages')
        .select('amount')
        .eq('user_id', user.id)
        .gte('created_at', monthAgo.toISOString());

      const monthCredits = monthUsage?.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0) || 0;

      setDailyStats({
        today_credits_used: todayCredits,
        week_credits_used: weekCredits,
        month_credits_used: monthCredits,
      });

      // Get credit usage history
      const { data: usages } = await supabase
        .from('credit_usages')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      setCreditUsages(usages || []);

    } catch (err) {
      console.error('Error fetching account data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  const calculateDaysLeft = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysLeft;
  };

  return (
    <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <AppHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">{t('pages.account.title')}</h1>
          <p className="text-gray-400">{t('pages.account.subtitle')}</p>
        </div>

        {/* Current Credits Card */}
        <div className="bg-gradient-to-br from-green-900/40 to-green-800/40 border-2 border-green-500/50 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-green-300 text-sm font-semibold mb-2">{t('pages.account.currentBalance')}</div>
              <div className="text-5xl font-bold text-white mb-2">{credits}</div>
              <div className="text-green-200 text-lg">{t('pages.account.creditsAvailable')}</div>
            </div>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center">
              <CreditCard className="w-12 h-12 text-green-400" />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {/* Today Usage */}
          <div className="bg-gradient-to-br from-red-900/30 to-red-800/30 border border-red-500/40 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                <Camera className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <div className="text-red-200 text-sm">{t('pages.account.todayUsage')}</div>
                <div className="text-white text-2xl font-bold">{dailyStats.today_credits_used}</div>
              </div>
            </div>
          </div>

          {/* Week Usage */}
          <div className="bg-gradient-to-br from-orange-900/30 to-orange-800/30 border border-orange-500/40 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <div className="text-orange-200 text-sm">{t('pages.account.weekUsage')}</div>
                <div className="text-white text-2xl font-bold">{dailyStats.week_credits_used}</div>
              </div>
            </div>
          </div>

          {/* Month Usage */}
          <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/30 border border-cyan-500/40 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <div className="text-cyan-200 text-sm">{t('pages.account.monthUsage')}</div>
                <div className="text-white text-2xl font-bold">{dailyStats.month_credits_used}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl mb-6">
          <div className="flex border-b border-zinc-800">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'overview'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <TrendingUp className="w-5 h-5" />
              {t('pages.account.tabs.overview')}
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'credits'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <CreditCard className="w-5 h-5" />
              {t('pages.account.tabs.credits')}
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'transactions'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <CheckCircle2 className="w-5 h-5" />
              {t('pages.account.tabs.transactions')}
            </button>
            <button
              onClick={() => setActiveTab('usages')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'usages'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <History className="w-5 h-5" />
              {t('pages.account.tabs.usages')}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">

            {/* Show message about credit sources if no lots */}
            {creditLots.length === 0 && credits > 0 && (
              <div className="bg-yellow-900/30 border border-yellow-800 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <Wallet className="w-8 h-8 text-yellow-400" />
                  <div>
                    <h4 className="text-yellow-400 font-bold mb-2">{t('pages.account.note.title')}</h4>
                    <p className="text-gray-300 text-sm">
                      {t('pages.account.note.message', { credits })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Credit Lots Details - Only show if exist */}
            {creditLots.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">{t('pages.account.creditDetails')}</h3>
                <div className="space-y-3">
                  {creditLots.map((lot) => {
                    const daysLeft = calculateDaysLeft(lot.expires_at);
                    const used = (lot as any).credit_usages?.reduce((sum: number, u: any) => sum + Math.abs(u.amount || 0), 0) || 0;
                    const available = lot.amount - used;
                    
                    return (
                      <div key={lot.id} className="bg-black/40 rounded-lg p-4 border-l-4 border-green-500">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                              <Wallet className="w-5 h-5 text-green-400" />
                            </div>
                            <div>
                              <div className="text-white font-semibold text-lg">{available} {t('pages.account.creditsAvailable')}</div>
                              <div className="text-gray-400 text-sm">{lot.reference || t('pages.account.creditsAdded')}</div>
                            </div>
                          </div>
                          <div className="text-left">
                            <div className={`text-sm font-bold ${daysLeft > 7 ? 'text-green-400' : daysLeft > 3 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {daysLeft > 0 ? t('pages.account.daysLeft', { days: daysLeft }) : t('pages.account.expired')}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>{t('pages.account.expires')}: {new Date(lot.expires_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="w-6 h-6 text-blue-400" />
                  <h4 className="text-gray-400 text-sm">{t('pages.account.avgDailyUsage')}</h4>
                </div>
                <div className="text-white text-3xl font-bold">
                  {dailyStats.month_credits_used > 0 
                    ? Math.round(dailyStats.month_credits_used / 30)
                    : 0}
                </div>
                <div className="text-gray-500 text-sm mt-2">{t('pages.account.creditsPerDay')}</div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <PiggyBank className="w-6 h-6 text-purple-400" />
                  <h4 className="text-gray-400 text-sm">{t('pages.account.totalCredits')}</h4>
                </div>
                <div className="text-white text-3xl font-bold">
                  {creditLots.reduce((sum, lot) => sum + lot.amount, 0) || 0}
                </div>
                <div className="text-gray-500 text-sm mt-2">{t('pages.account.creditsAdded')}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'credits' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="p-6 border-b border-zinc-800">
              <h3 className="text-xl font-bold text-white">{t('pages.account.allCredits')}</h3>
              <p className="text-gray-400 text-sm mt-1">{t('pages.account.allCreditsDesc')}</p>
            </div>
            
            {creditLots.length === 0 ? (
              <div className="p-12 text-center">
                <div className="flex justify-center mb-4">
                  <CreditCard className="w-16 h-16 text-gray-600" />
                </div>
                <p className="text-gray-500">{t('pages.account.noCredits')}</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {creditLots.map((lot) => {
                  const daysLeft = calculateDaysLeft(lot.expires_at);
                  const used = (lot as any).credit_usages?.reduce((sum: number, u: any) => sum + Math.abs(u.amount || 0), 0) || 0;
                  const available = lot.amount - used;
                  
                  return (
                    <div key={lot.id} className="p-6 hover:bg-zinc-800/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center">
                            {lot.source === 'admin_grant' ? (
                              <DollarSign className="w-7 h-7 text-blue-400" />
                            ) : (
                              <CreditCard className="w-7 h-7 text-blue-400" />
                            )}
                          </div>
                          <div>
                            <div className="text-white font-semibold">{lot.reference || t('pages.account.creditsAdded')}</div>
                            <div className="flex items-center gap-4 text-gray-500 text-sm mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {t('pages.account.from')}: {new Date(lot.created_at).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                            </div>
                            <div className="text-gray-500 text-sm">
                              {t('pages.account.expires')}: {new Date(lot.expires_at).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="text-left">
                          <div className="text-2xl font-bold text-green-400">
                            {available} / {lot.amount}
                          </div>
                          <div className="text-gray-500 text-xs">{t('pages.account.creditsAvailable')}</div>
                          <div className={`text-xs mt-2 font-bold ${daysLeft > 7 ? 'text-green-400' : daysLeft > 3 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {daysLeft > 0 ? t('pages.account.daysRemaining', { days: daysLeft }) : t('pages.account.expired')}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="p-6 border-b border-zinc-800">
              <h3 className="text-xl font-bold text-white">{t('pages.account.successfulTransactions')}</h3>
              <p className="text-gray-400 text-sm mt-1">{t('pages.account.completedPurchasesOnly')}</p>
            </div>
            
            {transactions.length === 0 ? (
              <div className="p-12 text-center">
                <div className="flex justify-center mb-4">
                  <CreditCard className="w-16 h-16 text-gray-600" />
                </div>
                <p className="text-gray-500 mb-4">{t('pages.account.noTransactions')}</p>
                <button
                  onClick={() => navigate('/credits')}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {t('pages.account.buyCreditsNow')}
                </button>
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="p-6 hover:bg-zinc-800/50 transition-colors">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                      {/* Transaction Info */}
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-semibold text-lg">
                              #{transaction.transaction_number}
                            </span>
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">{t('pages.account.completed')}</span>
                          </div>
                          <div className="text-gray-500 text-sm">
                            {new Date(transaction.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                      
                      {/* Credits & Amount */}
                      <div className="text-center md:text-left">
                        <div className="text-2xl font-bold text-green-400 mb-1">
                          +{transaction.credits_amount}
                        </div>
                        <div className="text-gray-500 text-sm">{t('pages.account.credits')}</div>
                      </div>
                      
                      {/* Price */}
                      <div className="text-right">
                        <div className="text-xl font-bold text-blue-400 mb-1">
                          ${transaction.price_amount}
                        </div>
                        <div className="text-gray-500 text-sm">{t('pages.account.purchasePrice')}</div>
                        {transaction.completed_at && (
                          <div className="text-gray-600 text-xs mt-2">
                            {new Date(transaction.completed_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'usages' && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="p-6 border-b border-zinc-800">
              <h3 className="text-xl font-bold text-white">{t('pages.account.creditLogs')}</h3>
              <p className="text-gray-400 text-sm mt-1">{t('pages.account.creditLogsDesc')}</p>
            </div>
            
            {creditUsages.length === 0 ? (
              <div className="p-12 text-center">
                <div className="flex justify-center mb-4">
                  <History className="w-16 h-16 text-gray-600" />
                </div>
                <p className="text-gray-500">{t('pages.account.noUsages')}</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {creditUsages.map((usage) => (
                  <div key={usage.id} className="p-6 hover:bg-zinc-800/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="w-6 h-6 text-red-400 rotate-180" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-red-400 font-bold text-xl">
                              -{Math.abs(usage.amount)}
                            </span>
                            <span className="text-gray-400 text-sm">كريدت</span>
                          </div>
                          <p className="text-white text-sm mb-2 break-words">{usage.reason}</p>
                          <div className="flex items-center gap-2 text-gray-500 text-xs">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(usage.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                            <span>•</span>
                            <span>
                              {new Date(usage.created_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AccountPage;
